import { Calendar, FileText } from 'lucide-react';
import { LiksDateRangeDialog } from '../../../components/liks/LiksDateRangeDialog/LiksDateRangeDialog';
import { LiksDropdown } from '../../../components/liks/LiksDropdown/LiksDropdown';
import { LiksIconButton } from '../../../components/liks/LiksIconButton/LiksIconButton';
import { useLacDocumentsTab } from '../../../features/lac/hooks/useLacDocumentsTab';
import { LacDocumentsAnalyticsTable } from '../LacDocumentsAnalyticsTable';
import './LacDocumentsTab.css';

export type LacDocumentsTabProps = {
  /** When set, list is scoped to this tenant via the API. */
  tenantName?: string;
};

export function LacDocumentsTab({ tenantName }: LacDocumentsTabProps = {}) {
  const {
    translate,
    showTenantFilter,
    tenantsQuery,
    filters,
    analyticsQuery,
    rows,
    fileSizeLabels,
    emptyValue,
    isEmpty,
  } = useLacDocumentsTab({ tenantName });

  return (
    <div className="lac-documents">
      <div className="lac-documents__toolbar">
        <div className="lac-documents__date-filter">
          <span className="lac-documents__date-label">
            {filters.rangeLabel}
          </span>
          <LiksIconButton
            variant="outlined"
            aria-label={translate('common.dateRange.open')}
            onClick={() => filters.setIsDateDialogOpen(true)}
          >
            <Calendar strokeWidth={2} aria-hidden />
          </LiksIconButton>
        </div>
        {showTenantFilter ? (
          <LiksDropdown
            className="lac-documents__tenant-filter"
            variant="on-light"
            value={filters.tenantFilter}
            onChange={filters.handleTenantFilterChange}
            options={filters.tenantFilterOptions}
            aria-label={translate('lac.documents.tenantFilter.label')}
            disabled={tenantsQuery.isLoading || tenantsQuery.isError}
          />
        ) : null}
        <p className="lac-documents__hint">
          <FileText
            className="lac-documents__hint-icon"
            strokeWidth={2}
            aria-hidden
          />
          {translate('lac.documents.createdHint')}
        </p>
      </div>

      <LiksDateRangeDialog
        isOpen={filters.isDateDialogOpen}
        fromDay={filters.fromDay}
        toDay={filters.toDay}
        onClose={() => filters.setIsDateDialogOpen(false)}
        onApply={filters.handleApplyDateRange}
      />

      {analyticsQuery.isLoading ? (
        <p className="lac-documents__status">
          {translate('lac.documents.loading')}
        </p>
      ) : null}

      {analyticsQuery.isError ? (
        <p className="lac-documents__status" role="alert">
          {translate('lac.documents.loadError')}
        </p>
      ) : null}

      {isEmpty ? (
        <p className="lac-documents__status">
          {filters.hasActiveTenantFilter || filters.dateRange != null
            ? translate('lac.documents.noResults')
            : translate('lac.documents.empty')}
        </p>
      ) : null}

      {!analyticsQuery.isLoading &&
      !analyticsQuery.isError &&
      rows.length > 0 ? (
        <LacDocumentsAnalyticsTable
          rows={rows}
          showTenant={showTenantFilter}
          fileSizeLabels={fileSizeLabels}
          emptyValue={emptyValue}
        />
      ) : null}
    </div>
  );
}
